import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/Button'
import { VocabularyBrain } from '@/components/ui/art/VocabularyBrain'
import { SrsSlider } from './SrsSlider'
import { fetchBrainSettings, updateBrainSettings } from '@/api/admin'
import type { BrainSettings } from '@/types/brainSettings'

// A representative field for the preview: dense enough to show the web, light
// enough to rebuild on every slider step.
const PREVIEW_COUNT = 900

const SLIDERS: {
  key: keyof BrainSettings
  label: string
  hint: string
  min: number
  max: number
  step: number
  suffix?: string
}[] = [
  {
    key: 'cluster_pull',
    label: 'Cluster pull',
    hint: 'How tightly each part of speech gathers toward its region. Zero is one even brain; higher draws nouns, verbs and the rest into separate lobes.',
    min: 0,
    max: 1,
    step: 0.05,
  },
  {
    key: 'link_distance',
    label: 'Edge distance',
    hint: 'How close two words must fall on screen before a line joins them. Higher weaves a denser web.',
    min: 16,
    max: 96,
    step: 2,
    suffix: 'px',
  },
]

function SettingsSkeleton() {
  return (
    <div className="space-y-6" aria-busy="true">
      <span className="sr-only">Loading brain settings</span>
      {SLIDERS.map((slider) => (
        <div key={slider.key} className="space-y-2">
          <div className="h-4 w-40 animate-pulse rounded bg-surface-bg" />
          <div className="h-6 w-full animate-pulse rounded bg-surface-bg" />
        </div>
      ))}
    </div>
  )
}

export function BrainTab({ active }: { active: boolean }) {
  const [draft, setDraft] = useState<BrainSettings | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    if (!active || draft) return
    fetchBrainSettings()
      .then((res) => setDraft(res.data.data))
      .catch(() => setLoadError('Could not load the brain settings. Reload to try again.'))
  }, [active, draft])

  function change(key: keyof BrainSettings, value: number) {
    setSaved(false)
    setSaveError(null)
    setDraft((current) => (current ? { ...current, [key]: value } : current))
  }

  function save() {
    if (!draft) return
    setSaving(true)
    setSaveError(null)
    updateBrainSettings(draft)
      .then(() => setSaved(true))
      .catch(() => setSaveError('Could not save. Try again.'))
      .finally(() => setSaving(false))
  }

  if (!active) return null
  if (loadError) return <p className="font-serif text-sm text-body">{loadError}</p>
  if (!draft) return <SettingsSkeleton />

  return (
    <div className="grid gap-12 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
      <section aria-labelledby="brain-params">
        <h2 id="brain-params" className="font-serif text-xl text-heading">
          Brain parameters
        </h2>
        <p className="mt-2 font-serif text-sm text-muted">
          Applies to every learner&rsquo;s vocabulary brain the next time it is drawn.
        </p>

        <div className="mt-8 space-y-7">
          {SLIDERS.map((slider) => (
            <SrsSlider
              key={slider.key}
              id={`brain-${slider.key}`}
              label={slider.label}
              hint={slider.hint}
              value={draft[slider.key]}
              min={slider.min}
              max={slider.max}
              step={slider.step}
              suffix={slider.suffix}
              onChange={(value) => change(slider.key, value)}
            />
          ))}
        </div>

        <div className="mt-8 flex items-center gap-4">
          <Button onClick={save} disabled={saving}>
            {saving ? 'Saving...' : 'Save parameters'}
          </Button>
          <p aria-live="polite" className="font-serif text-sm text-muted">
            {saveError ?? (saved ? 'Saved.' : '')}
          </p>
        </div>
      </section>

      <section aria-labelledby="brain-preview" className="lg:sticky lg:top-24 lg:self-start">
        <h2 id="brain-preview" className="font-serif text-xl text-heading">
          Preview
        </h2>
        <p className="mt-2 font-serif text-sm text-muted">
          A sample brain drawn with the values above. Move a slider and it redraws.
        </p>
        <div className="relative mt-8 h-[clamp(320px,40vw,480px)] overflow-hidden rounded-md">
          <VocabularyBrain
            count={PREVIEW_COUNT}
            clusterPull={draft.cluster_pull}
            linkDistance={draft.link_distance}
            className="absolute inset-0 h-full w-full"
          />
        </div>
      </section>
    </div>
  )
}
